import { CheckCircle2 } from 'lucide-react';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { getSymptomDisplayName } from '@/utils/medicalTerms';

/**
 * Historique complet des check-ins du patient
 * Utilisé par NursePatientDetail et DoctorPatientDetail
 */

const SEVERITY_LABELS = { 1: "Léger", 2: "Modéré", 3: "Sévère" };

const SEVERITY_COLORS = {
  1: "bg-yellow-100 text-yellow-700 border-yellow-300",
  2: "bg-orange-100 text-orange-700 border-orange-300",
  3: "bg-red-100 text-red-700 border-red-300"
};

const LEGACY_COLORS = {
  ROUGE: "bg-red-100 text-red-700",
  ORANGE: "bg-orange-100 text-orange-700",
  VERT: "bg-green-100 text-green-700"
};

export default function CheckinsHistory({ checkins }) {
  return (
    <div className="dash-card">
      <h3 className="font-semibold text-slate-800 mb-4">Historique des check-ins</h3>
      {checkins.length === 0 ? (
        <p className="text-slate-400 text-center py-8">Aucun check-in</p>
      ) : (
        <div className="space-y-4">
          {checkins.map(c => {
            const hasScoredSymptoms = c.symptoms && Object.keys(c.symptoms).length > 0;
            const hasPositiveSymptoms = hasScoredSymptoms && Object.values(c.symptoms).some(v => Number(v) > 0);
            const noUrgentSymptoms = !c.symptomsUrgent || !Object.keys(c.symptomsUrgent).some(k => c.symptomsUrgent[k]);
            const noLegacyMain = !c.symptomsMain || Object.keys(c.symptomsMain).length === 0;
            const patientIsWell = (c.alertLevel || c.status || "").toUpperCase() === "VERT" && (!hasScoredSymptoms || !hasPositiveSymptoms) && noUrgentSymptoms && noLegacyMain;
            return (
            <div key={c.id} className="border border-slate-200 rounded-xl overflow-hidden">
              {/* Header */}
              <div className="p-4 bg-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm font-medium text-slate-800">
                    {new Date(c.createdAt).toLocaleDateString("fr-FR", {
                      weekday: "long", day: "numeric", month: "long", year: "numeric",
                      hour: "2-digit", minute: "2-digit"
                    })}
                  </span>
                  {c.filledBy === "AGENT_APPEL" && (
                    <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 text-xs font-medium rounded-full" data-testid="proxy-badge">
                      Appel tél. {c.performedByName ? `— ${c.performedByName}` : (c.agentName ? `(${c.agentName})` : "")}
                    </span>
                  )}
                  {c.filledBy !== "AGENT_APPEL" && c.performedByName && (
                    <span
                      className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs font-medium rounded-full border border-amber-100"
                      data-testid="performed-by-badge"
                    >
                      Réalisé par {c.performedByName}
                    </span>
                  )}
                </div>
                <StatusBadge status={c.alertLevel || c.status || "STABLE"} size="sm" />
              </div>

              <div className="p-4 space-y-4">
                {patientIsWell && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 flex items-center gap-2" data-testid="checkin-history-well-highlight">
                    <CheckCircle2 className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                    <span className="text-sm font-bold text-emerald-900">Je vais bien — aucun symptôme signalé</span>
                  </div>
                )}
                {/* Symptômes urgents */}
                {c.symptomsUrgent && Object.keys(c.symptomsUrgent).some(k => c.symptomsUrgent[k]) && (
                  <div>
                    <p className="text-xs font-medium text-red-700 mb-2">SYMPTÔMES URGENTS</p>
                    <div className="flex flex-wrap gap-2">
                      {c.symptomsUrgent.dyspnea && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg">Dyspnée</span>}
                      {c.symptomsUrgent.chestPain && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg">Douleur thoracique</span>}
                      {c.symptomsUrgent.fever && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg">Fièvre</span>}
                      {c.symptomsUrgent.bleeding && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg">Saignement</span>}
                      {c.symptomsUrgent.confusion && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg">Confusion</span>}
                      {c.symptomsUrgent.severeVomiting && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg">Vomissements sévères</span>}
                      {c.symptomsUrgent.severeDiarrhea && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg">Diarrhée sévère</span>}
                      {c.symptomsUrgent.severePain && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg">Douleur sévère</span>}
                    </div>
                  </div>
                )}

                {/* Symptômes principaux (legacy) */}
                {c.symptomsMain && Object.keys(c.symptomsMain).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-amber-700 mb-2">SYMPTÔMES PRINCIPAUX</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {Object.entries(c.symptomsMain).map(([key, value]) => (
                        <div key={key} className={`px-2 py-1.5 rounded-lg text-xs font-medium ${LEGACY_COLORS[value] || "bg-slate-100 text-slate-600"}`}>
                          {getSymptomDisplayName(key)}: {value}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* NEW FORMAT: Score-based symptoms (0-3) */}
                {c.symptoms && Object.keys(c.symptoms).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-700 mb-2">EFFETS INDÉSIRABLES</p>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      {Object.entries(c.symptoms).filter(([_, v]) => v > 0).map(([key, value]) => (
                        <div key={key} className={`px-2 py-1.5 rounded-lg border text-xs font-medium ${SEVERITY_COLORS[value] || "bg-slate-100 text-slate-600"}`}>
                          {getSymptomDisplayName(key)}: {SEVERITY_LABELS[value] || value}
                        </div>
                      ))}
                      {Object.entries(c.symptoms).filter(([_, v]) => v > 0).length === 0 && (
                        <p className="text-sm font-semibold text-emerald-700 col-span-full">Aucun symptôme signalé</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Constantes vitales */}
                <div>
                  <p className="text-xs font-medium text-slate-600 mb-2">CONSTANTES VITALES</p>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    <div className="p-2 bg-slate-50 rounded-lg text-center">
                      <p className="text-lg font-bold text-slate-800">{c.vitals?.temperature || "—"}°C</p>
                      <p className="text-xs text-slate-500">Temp.</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg text-center">
                      <p className="text-lg font-bold text-slate-800">{c.vitals?.heartRate || "—"}</p>
                      <p className="text-xs text-slate-500">Pouls</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg text-center">
                      <p className="text-lg font-bold text-slate-800">{c.vitals?.spo2 || c.vitals?.spO2 || "—"}%</p>
                      <p className="text-xs text-slate-500">SpO2</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg text-center">
                      <p className="text-lg font-bold text-slate-800">{c.vitals?.weight || "—"}</p>
                      <p className="text-xs text-slate-500">Poids</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg text-center">
                      <p className="text-lg font-bold text-slate-800">
                        {c.vitals?.systole && c.vitals?.diastole 
                          ? `${c.vitals.systole}/${c.vitals.diastole}`
                          : c.vitals?.bloodPressure || "—"}
                      </p>
                      <p className="text-xs text-slate-500">Tension</p>
                    </div>
                    {c.imc && (
                      <div className="p-2 bg-blue-50 rounded-lg text-center">
                        <p className="text-lg font-bold text-blue-800">{c.imc.value}</p>
                        <p className="text-xs text-blue-600">IMC</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Raisons d'alerte */}
                {c.alertReasons && c.alertReasons.length > 0 && (
                  <div className="p-2 bg-amber-50 rounded-lg">
                    <p className="text-xs font-medium text-amber-700">Règles déclenchées :</p>
                    <p className="text-xs text-amber-600 mt-1">
                      {c.alertReasons.map(r => r.replace("rule:", "").trim()).join(" • ")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
}
